import { useState, useEffect } from 'react';
import { Plan, EMPTY_PLAN } from '../types';
import { supabase } from '../../../../lib/supabase';
import { logAudit } from '../../../../lib/audit';
import PlanEditorGeneral from './PlanEditorGeneral';
import PlanEditorPricing from './PlanEditorPricing';
import PlanEditorLimits from './PlanEditorLimits';
import PlanEditorFeatures from './PlanEditorFeatures';
import PlanEditorBilling from './PlanEditorBilling';

interface Props {
  plan: Plan | null;
  onClose: () => void;
  onSaved: () => void;
}

type Tab = 'geral' | 'precos' | 'limites' | 'recursos' | 'cobranca';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'geral', label: 'Geral', icon: 'ri-information-line' },
  { id: 'precos', label: 'Preços', icon: 'ri-money-dollar-circle-line' },
  { id: 'limites', label: 'Limites', icon: 'ri-dashboard-line' },
  { id: 'recursos', label: 'Recursos', icon: 'ri-puzzle-line' },
  { id: 'cobranca', label: 'Cobrança', icon: 'ri-secure-payment-line' },
];

const PlanEditor = ({ plan, onClose, onSaved }: Props) => {
  const isNew = !plan;
  const [form, setForm] = useState<Omit<Plan, 'id'>>(plan ? { ...plan } : { ...EMPTY_PLAN });
  const [activeTab, setActiveTab] = useState<Tab>('geral');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(plan ? { ...plan } : { ...EMPTY_PLAN });
    setActiveTab('geral');
    setError('');
  }, [plan]);

  const set = (fields: Partial<Omit<Plan, 'id'>>) => setForm((prev) => ({ ...prev, ...fields }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      setError('Nome e slug são obrigatórios.');
      setActiveTab('geral');
      return;
    }
    setSaving(true);
    setError('');

    if (isNew) {
      const { data, error: err } = await supabase.from('plans').insert(form).select().maybeSingle();
      if (err) { setError(err.message); setSaving(false); return; }
      await logAudit({ action: 'create', resource: 'plan', resource_id: data?.id, details: { name: form.name } });
    } else {
      const { error: err } = await supabase.from('plans').update(form).eq('id', plan!.id);
      if (err) { setError(err.message); setSaving(false); return; }
      await logAudit({ action: 'update', resource: 'plan', resource_id: plan!.id, details: { name: form.name } });
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-stretch justify-end z-50">
      {/* Overlay click */}
      <div className="flex-1" onClick={onClose} />

      {/* Panel */}
      <div className="w-[700px] bg-[#14142A] border-l border-white/10 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <div>
            <h2 className="text-[17px] font-700 text-white">
              {isNew ? 'Novo Plano' : `Editar: ${plan?.name}`}
            </h2>
            {!isNew && (
              <p className="text-[12px] text-white/40 font-mono mt-0.5">{plan?.slug}</p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/50 cursor-pointer transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 py-2 border-b border-white/10 shrink-0">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-violet-600/20 text-violet-400'
                  : 'text-white/40 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              <i className={`${tab.icon} text-[13px]`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === 'geral' && <PlanEditorGeneral form={form} set={set} />}
          {activeTab === 'precos' && <PlanEditorPricing form={form} set={set} />}
          {activeTab === 'limites' && <PlanEditorLimits form={form} set={set} />}
          {activeTab === 'recursos' && <PlanEditorFeatures form={form} set={set} />}
          {activeTab === 'cobranca' && <PlanEditorBilling form={form} set={set} />}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-6 py-4 border-t border-white/10 bg-[#14142A]">
          {error && (
            <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-[12px]">
              <i className="ri-error-warning-line" />{error}
            </div>
          )}
          {/* Quick overview */}
          <div className="flex items-center gap-4 mb-3 text-[11px] text-white/40">
            {form.is_active ? <span className="text-emerald-400">● Ativo</span> : <span className="text-red-400">● Inativo</span>}
            {form.is_featured && <span className="text-violet-400">★ Destaque</span>}
            {form.is_recommended && <span className="text-cyan-400">✓ Recomendado</span>}
            {form.trial_available && <span>Trial {form.trial_days}d</span>}
            {!form.is_public && <span className="text-orange-400">Oculto</span>}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 text-[14px] font-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-[14px] font-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {saving ? 'Salvando...' : isNew ? 'Criar Plano' : 'Salvar Alterações'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanEditor;
